/**
 * md-chat-launcher.js -- HubSpot click-to-chat (snippets/md-chat-launcher.liquid).
 *
 * The HubSpot chat widget no longer loads on page view
 * (hsConversationsSettings.loadImmediately = false, set inline in
 * layout/theme.liquid), because it kept running after a banner Decline. This
 * loads it only when the visitor presses "Chat with us".
 *
 * Flow: click -> button reads "Loading chat…" ->
 * HubSpotConversations.widget.load({ widgetOpen: true }) (straight away if
 * HubSpot is ready, else via window.hsConversationsOnReady) -> widgetLoaded ->
 * the chat PANEL is open, focus moves into it, then the launcher hides.
 * widgetClosed -> widget.remove(), launcher shown, focus back on the button.
 * If the widget never arrives (script blocked, network), a visible status
 * says "Chat could not load. Call us at <phone>." after FAIL_MS.
 *
 * HubSpot timing, measured on prod 2026-10-22 (cold load ~9s):
 * - widget.open() called inside the widgetLoaded handler is IGNORED: only the
 *   100x96 bubble showed, the launcher hid, and the visitor had to click the
 *   bubble again. load({ widgetOpen: true }) opens the panel itself, and
 *   ensureOpen() re-calls open() until the panel is really there.
 * - widget.remove() called inside the widgetClosed handler is IGNORED too: the
 *   bubble stayed on screen next to the re-shown launcher. It is deferred past
 *   HubSpot's close animation (REMOVE_DELAY_MS) and retried.
 *
 * widget.remove() on close, rather than leaving HubSpot's own bubble on screen,
 * keeps ONE launcher in the bottom-left slot. A later click loads it again.
 * While the panel is open, <html> carries .md-chat-open so other fixed widgets
 * (the willitfit FAB) can step out of its way.
 */
(function () {
  'use strict';

  var FAIL_MS = 15000; // HubSpot measured ~10-11s on a cold load (staging UAT 2026-10-10); 8s showed a false failure
  var ANNOUNCE_DELAY_MS = 100;
  var FOCUS_RETRY_MS = [0, 150, 400, 1000, 2000];
  var REMOVE_DELAY_MS = [400, 1000, 2000];
  var BUBBLE_POLL_MS = 100;
  var PANEL_MIN_HEIGHT = 120; // px: the closed bubble measures 96px, the open panel ~800px
  var CHAT_CONTAINER_ID = 'hubspot-messages-iframe-container';
  var OPEN_CLASS = 'md-chat-open';
  var LABEL_IDLE = 'Chat with us';
  var LABEL_LOADING = 'Loading chat…';

  // HubSpot's chatflow is set to not show on mobile devices: on a phone user
  // agent widget.load() never mounts anything (staging UAT 2026-10-10; live
  // already showed phones no chat). Offering a button that always fails is
  // worse than no button, so the launcher stays hidden on phones, matching
  // what HubSpot itself does. Re-enable by turning mobile chat on in HubSpot
  // and removing this check.
  function isPhone(nav) {
    nav = nav || (typeof navigator !== 'undefined' ? navigator : {});
    try {
      if (nav.userAgentData && typeof nav.userAgentData.mobile === 'boolean') {
        return nav.userAgentData.mobile;
      }
    } catch (_) {}
    return /iPhone|iPod|Android.+Mobile|Windows Phone|Mobile Safari/i.test(
      nav.userAgent || ''
    );
  }

  function init(root) {
    if (!root || root.__mdChatInit) return null;
    var button = root.querySelector('[data-md-chat-open]');
    var card = root.querySelector('[data-md-chat-card]');
    var status = root.querySelector('[data-md-chat-status]');
    var label = root.querySelector('.md-chat-launcher__label');
    if (!button || !card || !status) return null;
    root.__mdChatInit = true;
    if (isPhone()) {
      root.hidden = true;
      return null;
    }

    var phone = root.getAttribute('data-phone') || '';
    var state = 'idle'; // idle | loading | open | closing | failed
    var failTimer = null;
    var announceTimer = null;
    var wiredApi = null;
    var watchTimer = null;
    var watchObserver = null;
    var queued = false;

    /* Contract C2: clear, short delay, then set, so a repeated message is
       announced again. An empty text just clears. */
    function announce(text) {
      clearTimeout(announceTimer);
      status.textContent = '';
      if (!text) return;
      announceTimer = setTimeout(function () {
        status.textContent = text;
      }, ANNOUNCE_DELAY_MS);
    }

    /* Sighted feedback on the button itself. Screen readers get the same
       words once, from the status region (aria-busy on a button is poorly
       supported and would double it). */
    function setBusy(busy) {
      if (busy) button.setAttribute('data-loading', '');
      else button.removeAttribute('data-loading');
      if (label) label.textContent = busy ? LABEL_LOADING : LABEL_IDLE;
    }

    /* HubSpot swaps window.HubSpotConversations for its full API once the
       widget has loaded; calls on the object captured at click time were
       silently dropped (remove() never ran on prod, UAT 2026-10-22). Always
       resolve the live object. */
    function live(api) {
      return window.HubSpotConversations || api;
    }

    function container() {
      return document.getElementById(CHAT_CONTAINER_ID);
    }

    /* True once HubSpot shows the chat PANEL, not just its closed bubble. */
    function panelOpen() {
      var c = container();
      if (!c) return false;
      try {
        return c.getBoundingClientRect().height > PANEL_MIN_HEIGHT;
      } catch (_) {
        return false;
      }
    }

    function setOpenClass(on) {
      try {
        document.documentElement.classList.toggle(OPEN_CLASS, on);
      } catch (_) {}
    }

    function fail() {
      if (state !== 'loading') return;
      state = 'failed';
      setBusy(false);
      status.classList.add('is-visible');
      announce(
        'Chat could not load.' + (phone ? ' Call us at ' + phone + '.' : '')
      );
    }

    /* Hand the bottom-left slot to HubSpot the moment its container exists.
       Its 96px bubble shows 0.3-0.9s before the panel (UAT 2026-10-22), and
       keeping our card up until the panel left two launchers on screen. The
       chat is a cross-origin iframe; focusing the iframe element moves focus
       into it, and the card (with the focused button) is hidden only once
       focus is IN it, so focus is never dropped to <body>. */
    function handOff() {
      if (card.hidden) return true;
      var frame = null;
      try {
        var c = container();
        frame = c && c.querySelector('iframe');
        if (frame && document.activeElement !== frame) frame.focus();
      } catch (_) {}
      if (frame && document.activeElement === frame) {
        card.hidden = true;
        setOpenClass(true);
        return true;
      }
      return false;
    }

    /* HubSpot appends its container to <body>: a MutationObserver hands off
       in the same task, before the browser paints, so the bubble and the
       card are never on screen together (a 100ms poll still showed one
       overlapping frame, UAT 2026-10-22). The poll is the fallback for a
       browser without MutationObserver or a container nested deeper. */
    function watchForBubble() {
      stopWatching();
      var check = function () {
        if ((state !== 'loading' && state !== 'open') || handOff()) {
          stopWatching();
        }
      };
      if (typeof MutationObserver !== 'undefined') {
        watchObserver = new MutationObserver(check);
        watchObserver.observe(document.body, { childList: true });
      }
      watchTimer = setInterval(check, BUBBLE_POLL_MS);
    }

    function stopWatching() {
      clearInterval(watchTimer);
      if (watchObserver) {
        watchObserver.disconnect();
        watchObserver = null;
      }
    }

    /* After widgetLoaded: keep asking open() while only the bubble shows
       (open() inside the widgetLoaded handler is ignored), and hand off. If
       the last retry still cannot move focus into the chat, the launcher
       stays visible with focus on the button, the status says the chat is
       open, and a press of the button tries again. */
    function focusWidget() {
      var settled = false;
      var last = FOCUS_RETRY_MS.length - 1;
      FOCUS_RETRY_MS.forEach(function (ms, i) {
        setTimeout(function () {
          if (state !== 'open' || settled) return;
          if (!panelOpen()) {
            try {
              if (wiredApi) live(wiredApi).widget.open();
            } catch (_) {}
          }
          var handed = handOff();
          if (handed && panelOpen()) {
            settled = true;
            return;
          }
          if (i === last) {
            settled = true;
            if (handed) return; // HubSpot's bubble is the launcher now
            // The panel may be up even though focus could not enter it: keep
            // other widgets off it, and say so on screen, not only to SRs.
            if (panelOpen()) setOpenClass(true);
            status.classList.add('is-visible');
            announce('Chat is open.');
          }
        }, ms);
      });
    }

    function opened(api) {
      if (state === 'open') return;
      clearTimeout(failTimer);
      state = 'open';
      setBusy(false);
      status.classList.remove('is-visible');
      try {
        live(api).widget.open();
      } catch (_) {}
      announce('');
      focusWidget();
    }

    /* Show the launcher only once HubSpot's bubble is gone, so the two never
       sit on screen together. If the bubble will not go, it stays as the one
       launcher (it reopens the chat itself) and ours stays hidden. */
    function closed(api) {
      if (state !== 'open') return;
      state = 'closing';
      var last = REMOVE_DELAY_MS.length - 1;
      REMOVE_DELAY_MS.forEach(function (ms, i) {
        setTimeout(function () {
          if (state !== 'closing') return;
          if (container()) {
            try {
              live(api).widget.remove();
            } catch (_) {}
          }
          var still = container();
          if (still && i < last) return;
          state = 'idle';
          setOpenClass(false);
          if (still) {
            try {
              var frame = still.querySelector('iframe');
              if (frame) frame.focus();
            } catch (_) {}
            return;
          }
          card.hidden = false;
          try {
            button.focus();
          } catch (_) {}
        }, ms);
      });
    }

    function wire(api) {
      if (wiredApi === api || typeof api.on !== 'function') return;
      wiredApi = api;
      api.on('widgetLoaded', function () {
        // A late arrival after the failure message still opens: they asked.
        if (state === 'loading' || state === 'failed') opened(api);
      });
      api.on('widgetClosed', function () {
        closed(api);
      });
    }

    function start(api) {
      if (!api || !api.widget) return;
      if (state !== 'loading' && state !== 'failed') return;
      wire(api);
      var st = {};
      try {
        st = (api.widget.status && api.widget.status()) || {};
      } catch (_) {}
      if (st.loaded) {
        opened(api);
        return;
      }
      try {
        api.widget.load({ widgetOpen: true });
      } catch (_) {
        fail();
      }
    }

    button.addEventListener('click', function () {
      if (state === 'loading' || state === 'closing') return;
      if (state === 'open') {
        // Only reachable when focus could not move into the chat (see
        // focusWidget): open it again and retry the focus move.
        try {
          if (wiredApi) live(wiredApi).widget.open();
        } catch (_) {}
        focusWidget();
        return;
      }
      state = 'loading';
      setBusy(true);
      status.classList.remove('is-visible');
      announce(LABEL_LOADING);
      clearTimeout(failTimer);
      failTimer = setTimeout(fail, FAIL_MS);
      watchForBubble();
      if (window.HubSpotConversations) {
        start(window.HubSpotConversations);
      } else if (!queued) {
        queued = true;
        (window.hsConversationsOnReady =
          window.hsConversationsOnReady || []).push(function () {
          start(window.HubSpotConversations);
        });
      }
    });

    return {
      state: function () {
        return state;
      },
    };
  }

  function boot() {
    try {
      init(document.querySelector('[data-md-chat-launcher]'));
    } catch (_) {
      /* the launcher must never break the page */
    }
  }

  window.mdChatLauncher = { init: init, isPhone: isPhone, FAIL_MS: FAIL_MS };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
